import type { OutboxOp } from '@pos/sync';
import type { LocalDb } from './db.ts';
import { patchRow, type WriteActor } from './repo.ts';

/** Cambios que el servidor rechazó (para mostrarlos y decidir qué hacer). */
export function rejectedOps(db: LocalDb): Promise<OutboxOp[]> {
  return db.outbox.where('status').equals('rejected').sortBy('seq');
}

/**
 * Vuelve a encolar cambios rechazados (p. ej. si el rechazo fue porque el servidor todavía no
 * tenía una actualización, o ya se corrigió el permiso). Conservan su lugar en la fila.
 */
export async function retryRejectedOps(db: LocalDb, seqs: number[]): Promise<number> {
  return db.outbox
    .where('seq')
    .anyOf(seqs)
    .filter((op) => op.status === 'rejected')
    .modify((op) => {
      op.status = 'pending';
      op.attempts = 0;
      op.next_attempt_at = 0;
      delete op.last_error;
    });
}

/**
 * Saca una operación de la cola y la deshace en este dispositivo: un alta se borra (una venta,
 * con sus movimientos de stock y de cuenta corriente). Una modificación ya se revirtió con la
 * versión del servidor (o se revierte en la próxima sincronización).
 */
export async function undoLocalOp(db: LocalDb, op: OutboxOp): Promise<void> {
  await db.transaction('rw', [db.outbox, db.syncTable(op.table), db.stock_movements, db.account_entries], async () => {
    await db.outbox.delete(op.seq!);
    if (op.kind !== 'insert') return;
    if (op.table === 'sales') {
      const items = (op.payload.items as Array<{ movement_id: string }> | undefined) ?? [];
      await db.stock_movements.bulkDelete(items.map((i) => i.movement_id));
      const payments = (op.payload.payments as Array<{ id: string }> | undefined) ?? [];
      await db.account_entries.bulkDelete(payments.map((p) => p.id));
    }
    await db.syncTable(op.table).delete(op.row_id);
  });
}

/**
 * Descarta un cambio rechazado: se deshace en este dispositivo y, si ya está en la cuarentena
 * del servidor, se marca descartado ahí también (así el dueño no lo ve como pendiente).
 */
export async function discardRejectedOp(db: LocalDb, seq: number, actor?: WriteActor): Promise<void> {
  const op = await db.outbox.get(seq);
  if (!op || op.status !== 'rejected') return;
  await undoLocalOp(db, op);
  const quarantined = await db.sync_rejections.get(op.op_id);
  if (actor && quarantined?.status === 'pending') {
    await patchRow(db, 'sync_rejections', op.op_id, { status: 'discarded' }, actor);
  }
}

/** Cambio pendiente de enviar más viejo (para avisar si hace mucho que no se envía). */
export async function oldestPendingOp(db: LocalDb): Promise<OutboxOp | undefined> {
  return db.outbox.where('status').equals('pending').first();
}
